import type { Http } from "../http";
import { ApiError } from "../errors";
import { isRecord } from "../http";
import { parseBody } from "../models/common";
import { accountDeletionSchema, catalogSchema, userPlanSchema, walletSchema, type AccountDeletion, type Catalog, type UserPlan } from "../models/plan";
import { memoryListSchema, preferencesSchema, type MemoryFact } from "../models/consult";
import { z } from "zod";

// Only https: a checkout url the browser will be sent to must never be a
// javascript: or data: url, whatever the server (or a proxy) returns.
const urlSchema = z.object({ url: z.url({ protocol: /^https$/ }) });

/** The one plan the web sells: monthly, $12.99 or ₹499 depending on where you are. */
export type BillingPlan = "monthly";

/** Plan, memory, preferences, wallet and the account itself. */
export function accountEndpoints(http: Http) {
  return {
    async fetchPlan(): Promise<UserPlan> {
      return parseBody(userPlanSchema, await http.get("/me/plan"));
    },

    async fetchMemory(): Promise<readonly MemoryFact[]> {
      return parseBody(memoryListSchema, await http.get("/me/memory")).facts;
    },

    async forgetMemory(id: number): Promise<void> {
      await http.del(`/me/memory/${id}`);
    },

    async setLanguagePreference(code: string): Promise<string> {
      const body = await http.post("/me/preferences", { language: code });
      return parseBody(preferencesSchema, body).language || code;
    },

    async getLanguagePreference(): Promise<string | null> {
      try {
        const body = await http.get("/me/preferences");
        return parseBody(preferencesSchema, body).language || null;
      } catch (error) {
        if (error instanceof ApiError && error.isNotFound) return null;
        throw error;
      }
    },

    /** Idempotent: re-agreeing returns the original date. */
    async recordAiConsent(): Promise<void> {
      await http.put("/me/ai-consent");
    },

    /**
     * Where to send the browser to pay. Rejects with 409 when already
     * subscribed and 503 when billing is not configured on this deployment.
     */
    async startCheckout(plan: BillingPlan = "monthly"): Promise<string> {
      return parseBody(urlSchema, await http.post("/billing/checkout", { plan })).url;
    },

    /** Where to send the browser to change or cancel. 404 until a checkout has happened. */
    async openBillingPortal(): Promise<string> {
      return parseBody(urlSchema, await http.post("/billing/portal", {})).url;
    },

    async fetchWalletBalance(): Promise<number> {
      return parseBody(walletSchema, await http.get("/wallet")).balance;
    },

    async fetchCatalog(): Promise<Catalog> {
      return parseBody(catalogSchema, await http.get("/catalog", { auth: false }));
    },

    /**
     * Deletes the account and everything under it. Rejects on any failure so
     * a delete that did not happen is never reported as one that did. The
     * caller must sign out afterwards.
     */
    async deleteAccount(): Promise<AccountDeletion> {
      const body = await http.del("/me");
      return parseBody(accountDeletionSchema, isRecord(body) ? body : {});
    },
  };
}
